import type { IssueReport } from '../../types/issue-triage';

/**
 * Every file and screenshot on one issue, as one list (L-1224 part 2): the capture the reporter's widget took, the
 * images the reporter attached, and the files the team added afterwards. The panel shows this list and Copy for
 * Claude hands it all over, so a file cannot be visible in one place and missing from the other.
 */

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_FILES = 25;

export type IssueFileSource = 'capture' | 'reporter' | 'team';

export interface IssueFileView {
  /** Unique within the issue (the url). */
  key: string;
  url: string;
  name: string;
  contentType: string;
  isImage: boolean;
  source: IssueFileSource;
  size?: number;
  addedByName?: string;
  addedAt?: Date;
}

/** Bidi overrides/isolates, zero-width characters, word joiners, soft hyphen, BOM: none belongs in a file name (mirrors the server). */
export const INVISIBLE_CHARS = /[\u00ad\u200b-\u200f\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff]/g;
export const cleanFileName = (name: string): string => name.replace(INVISIBLE_CHARS, '');

/** This project's Storage bucket (the one the app uploads to). Empty when the build has none: nothing is then vouched for. */
export const ownStorageBucket = (): string => process.env.REACT_APP_FIREBASE_STORAGE_BUCKET ?? '';

const toDate = (v: unknown): Date | undefined => {
  if (v instanceof Date) return v;
  const t = v as { toDate?: () => Date } | null | undefined;
  return typeof t?.toDate === 'function' ? t.toDate() : undefined;
};

/** capture first, then the reporter's images, then the team's files (oldest first). Blank/duplicate urls are dropped. */
export function issueFileList(issue: Pick<IssueReport, 'screenshotUrl' | 'attachmentUrls' | 'attachments'>): IssueFileView[] {
  const out: IssueFileView[] = [];
  const seen = new Set<string>();
  const push = (f: IssueFileView) => {
    // Only https links are ever offered as a link (an old or odd value such as a javascript: URL is dropped).
    if (!f.url || !f.url.startsWith('https://') || seen.has(f.url)) return;
    seen.add(f.url);
    out.push(f);
  };
  if (typeof issue.screenshotUrl === 'string') {
    push({ key: issue.screenshotUrl, url: issue.screenshotUrl, name: 'Screenshot', contentType: 'image/png', isImage: true, source: 'capture' });
  }
  (issue.attachmentUrls ?? []).forEach((u, i) => {
    if (typeof u === 'string') push({ key: u, url: u, name: `Reporter attachment ${i + 1}`, contentType: 'image/*', isImage: true, source: 'reporter' });
  });
  for (const a of issue.attachments ?? []) {
    if (!a || typeof a.url !== 'string') continue;
    const type = typeof a.contentType === 'string' && /^[a-z0-9][a-z0-9.+-]*\/[a-z0-9][a-z0-9.+-]*$/i.test(a.contentType) ? a.contentType : 'application/octet-stream';
    push({
      key: a.url,
      url: a.url,
      name: typeof a.name === 'string' && cleanFileName(a.name) ? cleanFileName(a.name) : 'File',
      contentType: type,
      isImage: type.toLowerCase().startsWith('image/'),
      source: 'team',
      size: typeof a.size === 'number' ? a.size : undefined,
      addedByName: a.addedBy?.name,
      addedAt: toDate(a.addedAt),
    });
  }
  return out;
}

const NOTE_LINK = /\[Attached file: ([^\]\n]{1,200})\]\((https:\/\/[^)\s]{1,2048})\)/g;
const STORAGE_HOST = 'firebasestorage.googleapis.com';
const ISSUE_FOLDER = 'issue-screenshots/';

const LEGACY_HOST = 'storage.googleapis.com';
/** Anything that can end or start a line, or hide in a URL (the URL parser silently drops tabs and newlines). Mirrors the server. */
// eslint-disable-next-line no-control-regex
const UNSAFE_URL_CHARS = /[\u0000-\u0020\u007f-\u009f\u2028\u2029]/;

/** The Storage object path of a URL (so one file matches across tokens), or the URL itself when it is not a Storage URL. */
export function fileKey(url: string): string {
  try {
    const u = new URL(url);
    const m = u.hostname === LEGACY_HOST
      ? /^\/[^/]+\/(issue-screenshots\/[^/]+)$/.exec(u.pathname)
      : /^\/v0\/b\/[^/]+\/o\/([^/]+)$/.exec(u.pathname);
    return m ? decodeURIComponent(m[1]) : url;
  } catch {
    return url;
  }
}

/**
 * Does this file name (the part after `issue-screenshots/`) belong to THIS issue? The text before the FIRST dot is the
 * issue id. Ids are chosen by the reporter, so `ISS-1-2.png` is NOT a file of `ISS-1`; the older panel's
 * `<id>-<13 digits>` names are not accepted (nothing stored uses them). Same rule as the server.
 */
export function fileBelongsToIssue(file: string, issueId: string): boolean {
  const dot = file.indexOf('.');
  return dot >= 1 && !file.includes('/') && file.slice(0, dot) === issueId;
}

/**
 * Is this URL one of THIS issue's own files in OUR bucket? A note can say anything, including a link to any web page or
 * to another project's bucket; only a Storage URL in this project's bucket under `issue-screenshots/` that passes the
 * same first-dot rule as the server is listed as a file for Claude to read.
 */
export function isThisIssuesStorageFile(url: string, issueId: string | undefined, bucket: string | undefined = ownStorageBucket()): boolean {
  if (!issueId || !bucket || UNSAFE_URL_CHARS.test(url)) return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' || u.port !== '' || u.username !== '' || u.password !== '') return false;
    let urlBucket: string;
    let encodedPath: string;
    if (u.hostname === STORAGE_HOST) {
      const m = /^\/v0\/b\/([^/]+)\/o\/([^/]+)$/.exec(u.pathname);
      if (!m) return false;
      [, urlBucket, encodedPath] = m;
    } else if (u.hostname === LEGACY_HOST) {
      const m = /^\/([^/]+)\/(issue-screenshots\/[^/]+)$/.exec(u.pathname);
      if (!m) return false;
      [, urlBucket, encodedPath] = m;
    } else {
      return false;
    }
    if (urlBucket !== bucket) return false;
    const path = decodeURIComponent(encodedPath);
    if (!path.startsWith(ISSUE_FOLDER) || path.includes('..') || path.includes('\\')) return false;
    return fileBelongsToIssue(path.slice(ISSUE_FOLDER.length), issueId);
  } catch {
    return false;
  }
}

type NoteLinkSource = Pick<IssueReport, 'id' | 'statusNotes' | 'screenshotUrl' | 'attachmentUrls' | 'attachments'>;

/**
 * Files that older versions of the panel only linked inside a NOTE ("[Attached file: name](url)"). They were never
 * moved onto the issue (nothing is migrated), so Copy for Claude lists them separately. Only this issue's own Storage
 * files count, and a file already listed as the capture, a reporter image or a team file is not listed twice; every
 * other link in a note is only counted (`otherNoteLinks`), never listed and never offered to be read.
 */
export function noteLinkedFilesDetailed(issue: NoteLinkSource, bucket: string = ownStorageBucket()): { files: { name: string; url: string }[]; otherLinks: number } {
  const files: { name: string; url: string }[] = [];
  const seen = new Set<string>(issueFileList(issue).map((f) => fileKey(f.url)));
  let otherLinks = 0;
  for (const n of issue.statusNotes ?? []) {
    if (typeof n?.text !== 'string') continue;
    for (const m of Array.from(n.text.matchAll(NOTE_LINK))) {
      if (!isThisIssuesStorageFile(m[2], issue.id, bucket)) {
        otherLinks += 1;
        continue;
      }
      const key = fileKey(m[2]);
      if (seen.has(key)) continue;
      seen.add(key);
      files.push({ name: cleanFileName(m[1]), url: m[2] });
    }
  }
  return { files, otherLinks };
}

/**
 * The files the TEAM added, split by whether each stored link checks out as a file of this issue in our bucket. Only
 * the verified ones are handed to Claude in Copy (the rest are counted, never listed or to be opened).
 */
export function verifiedTeamFiles(issue: Pick<IssueReport, 'id' | 'attachments'>, bucket: string = ownStorageBucket()): { files: IssueFileView[]; leftOut: number } {
  const all = issueFileList({ attachments: issue.attachments }).filter((f) => f.source === 'team');
  const files = all.filter((f) => isThisIssuesStorageFile(f.url, issue.id, bucket));
  return { files, leftOut: all.length - files.length };
}

export function noteLinkedFiles(issue: NoteLinkSource): { name: string; url: string }[] {
  return noteLinkedFilesDetailed(issue).files;
}

/**
 * Same deny list as the server (BLOCKED_TYPE) and storage.rules isSafeIssueUploadType: content that can run script when
 * opened inline. Anchored on the type, so Office files (application/vnd.openxmlformats-officedocument.*) stay allowed.
 */
const BLOCKED = [
  /^text\/(x-)?(x?html|xml|xsl|javascript|ecmascript|jscript|livescript|vbscript)/,
  /^application\/(x-)?(xhtml|xml|xslt?|javascript|ecmascript)/,
  /^image\/svg/,
  /\+xml$/,
];

/** Why a file cannot be attached, in plain words; null when it can. Checked before uploading so a person is told at once. */
export function checkUploadable(file: { name: string; type: string; size: number }, alreadyOnIssue: number): string | null {
  if (alreadyOnIssue >= MAX_FILES) return `An issue can hold at most ${MAX_FILES} added files.`;
  if (file.size <= 0) return `${file.name} is empty.`;
  if (file.size > MAX_FILE_BYTES) return `${file.name} is larger than 20 MB.`;
  const type = (file.type || '').split(';')[0].trim().toLowerCase();
  if (BLOCKED.some((re) => re.test(type)) || /\.(html?|svg|xml|xhtml|xsl|xslt)$/i.test(file.name)) {
    return `${file.name} is a kind of file that cannot be attached (web pages and SVG can run code).`;
  }
  return null;
}

/** The Storage path for a new file: only this issue's own prefix, unique per file, a safe extension. */
export function storagePathFor(issueId: string, fileName: string, now: number, random: string): string {
  const ext = (/\.([A-Za-z0-9]{1,8})$/.exec(fileName)?.[1] ?? 'bin').toLowerCase();
  return `issue-screenshots/${issueId}.d${now}${random.replace(/[^a-z0-9]/gi, '').slice(0, 6)}.${ext}`;
}

export function formatBytes(n: number | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
